import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getUsersFaceStatus, adminRegisterFace, adminDeleteFaceReference,
  getSekolahList 
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import FaceVerification from '../ui/FaceVerification';
import { 
  ChevronLeft, ChevronDown, ChevronRight, UserCheck, Users, Search, Loader2, 
  ShieldCheck, ShieldAlert, Camera, Trash2, RefreshCw,
  CheckCircle2, XCircle, Building2, Filter, MapPin
} from 'lucide-react';
import { toast } from '../ui/sonner';

// Daftar Distrik Kabupaten Asmat
const DISTRIK_LIST = [
  "Agats", "Akat", "Atsy", "Ayip", "Betcbamu", "Fayit", "Jetsy", 
  "Kolf Brasa", "Kopay", "Pantai Kasuari", "Pulau Tiga", "Sawa Erma", 
  "Sirets", "Suator", "Suru-suru", "Ti-Zain", "Unir Sirau"
];

const FaceRegistrationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Data states
  const [users, setUsers] = useState([]);
  const [sekolahList, setSekolahList] = useState([]);
  const [stats, setStats] = useState({ total: 0, with_face: 0, without_face: 0 });
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDistrik, setFilterDistrik] = useState('all');
  const [filterFaceStatus, setFilterFaceStatus] = useState('all');
  
  // Expanded schools state
  const [expandedSekolah, setExpandedSekolah] = useState(null);
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Check if user has permission
  const canAccess = ['admin_dinas', 'kepala_sekolah'].includes(user?.role);
  const isKepalaSekolah = user?.role === 'kepala_sekolah';

  useEffect(() => {
    if (canAccess) {
      loadData();
    }
  }, [canAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get sekolah list for admin
      let schools = [];
      if (!isKepalaSekolah) {
        schools = await getSekolahList();
        setSekolahList(schools);
      }
      
      // Get users face status (get all users for grouping)
      const sekolahId = isKepalaSekolah ? user.sekolah_id : null;
      const data = await getUsersFaceStatus(sekolahId);
      
      setUsers(data.users);
      setStats({
        total: data.total,
        with_face: data.with_face,
        without_face: data.without_face
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  // Group users by school with stats
  const groupedBySekolah = useMemo(() => {
    const groups = {};
    
    users.forEach(u => {
      const sekolahId = u.sekolah_id;
      if (!groups[sekolahId]) {
        // Find school info from sekolahList
        const schoolInfo = sekolahList.find(s => s.id === sekolahId) || {};
        groups[sekolahId] = {
          sekolah_id: sekolahId,
          sekolah_nama: u.sekolah_nama || 'Unknown',
          distrik: schoolInfo.distrik || '',
          kampung: schoolInfo.kampung || '',
          users: [],
          stats: {
            total: 0,
            with_face: 0,
            without_face: 0
          }
        };
      }
      
      groups[sekolahId].users.push(u);
      groups[sekolahId].stats.total++;
      if (u.has_face) {
        groups[sekolahId].stats.with_face++;
      } else {
        groups[sekolahId].stats.without_face++;
      }
    });
    
    return Object.values(groups).sort((a, b) => a.sekolah_nama.localeCompare(b.sekolah_nama));
  }, [users, sekolahList]);

  // Filter schools based on search and distrik
  const filteredGroups = useMemo(() => {
    return groupedBySekolah.filter(group => {
      // Distrik filter
      if (filterDistrik !== 'all' && group.distrik !== filterDistrik) {
        return false;
      }
      
      // Search filter (search in school name or kampung)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchSchool = group.sekolah_nama.toLowerCase().includes(query);
        const matchKampung = group.kampung?.toLowerCase().includes(query);
        const matchUser = group.users.some(u => 
          u.nama.toLowerCase().includes(query) || 
          u.nip_nuptk?.toLowerCase().includes(query)
        );
        if (!matchSchool && !matchKampung && !matchUser) {
          return false;
        }
      }
      
      // Face status filter
      if (filterFaceStatus === 'with' && group.stats.with_face === 0) return false;
      if (filterFaceStatus === 'without' && group.stats.without_face === 0) return false;
      
      return true;
    });
  }, [groupedBySekolah, filterDistrik, searchQuery, filterFaceStatus]);

  // Filter users within a school based on face status
  const getFilteredUsersInSchool = (schoolUsers) => {
    return schoolUsers.filter(u => {
      // Search filter for user
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!u.nama.toLowerCase().includes(query) && 
            !u.nip_nuptk?.toLowerCase().includes(query)) {
          // Check if search matches school - if so, show all users
          const parentGroup = groupedBySekolah.find(g => g.sekolah_id === u.sekolah_id);
          if (parentGroup && 
              !parentGroup.sekolah_nama.toLowerCase().includes(query) &&
              !parentGroup.kampung?.toLowerCase().includes(query)) {
            return false;
          }
        }
      }
      
      // Face status filter
      if (filterFaceStatus === 'with' && !u.has_face) return false;
      if (filterFaceStatus === 'without' && u.has_face) return false;
      
      return true;
    });
  };

  const toggleSekolah = (sekolahId) => {
    setExpandedSekolah(expandedSekolah === sekolahId ? null : sekolahId);
  };

  // Calculate filtered stats
  const filteredStats = useMemo(() => {
    let total = 0, with_face = 0, without_face = 0;
    filteredGroups.forEach(g => {
      const filteredUsers = getFilteredUsersInSchool(g.users);
      total += filteredUsers.length;
      with_face += filteredUsers.filter(u => u.has_face).length;
      without_face += filteredUsers.filter(u => !u.has_face).length;
    });
    return { total, with_face, without_face };
  }, [filteredGroups, filterFaceStatus, searchQuery]);

  const handleOpenRegistration = (targetUser) => {
    setSelectedUser(targetUser);
    setShowRegistrationModal(true);
  };

  const handleFaceCapture = async (result) => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await adminRegisterFace({
        user_id: selectedUser.id,
        foto_base64: result.photo,
        face_descriptor: result.descriptor
      });
      
      toast.success(`Wajah ${selectedUser.nama} berhasil didaftarkan!`);
      setShowRegistrationModal(false);
      setSelectedUser(null);
      loadData(); // Refresh list
    } catch (error) {
      console.error('Face registration error:', error);
      toast.error(error.response?.data?.detail || 'Gagal mendaftarkan wajah');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFace = async (targetUser) => {
    if (!window.confirm(`Hapus data wajah ${targetUser.nama}? Guru perlu didaftarkan ulang untuk verifikasi wajah.`)) {
      return;
    }
    
    try {
      await adminDeleteFaceReference(targetUser.id);
      toast.success(`Data wajah ${targetUser.nama} berhasil dihapus`);
      loadData();
    } catch (error) {
      toast.error('Gagal menghapus data wajah');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterDistrik('all');
    setFilterFaceStatus('all');
  };

  // Render for Kepala Sekolah (single school, flat list)
  const renderSingleSchoolView = () => {
    const filteredUsers = users.filter(u => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!u.nama.toLowerCase().includes(query) && 
            !u.nip_nuptk?.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (filterFaceStatus === 'with' && !u.has_face) return false;
      if (filterFaceStatus === 'without' && u.has_face) return false;
      return true;
    });

    return (
      <Card className="border-indigo-100/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-indigo-600" />
            Daftar Guru ({filteredUsers.length})
          </CardTitle>
          <CardDescription>
            Klik tombol kamera untuk mendaftarkan wajah guru
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Tidak ada data pengguna</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map((u) => renderUserItem(u))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render user item (reusable)
  const renderUserItem = (u) => (
    <div 
      key={u.id}
      className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
        u.has_face 
          ? 'bg-emerald-50/50 border-emerald-200 hover:bg-emerald-50' 
          : 'bg-amber-50/50 border-amber-200 hover:bg-amber-50'
      }`}
      data-testid={`user-face-item-${u.id}`}
    >
      <div className="flex items-center gap-4">
        {/* Avatar/Photo */}
        <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden ${
          u.has_face ? 'bg-emerald-100' : 'bg-amber-100'
        }`}>
          {u.has_face ? (
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
          ) : (
            <ShieldAlert className="w-6 h-6 text-amber-600" />
          )}
        </div>
        
        {/* Info */}
        <div>
          <p className="font-medium text-indigo-950">{u.nama}</p>
          <p className="text-sm text-gray-500">{u.nip_nuptk}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs">
              {u.role === 'guru' ? 'Guru' : 
               u.role === 'kepala_sekolah' ? 'Kepsek' : 
               u.role === 'tenaga_tata_usaha' ? 'TU' :
               u.role === 'pengawas' ? 'Pengawas' : u.role}
            </Badge>
          </div>
        </div>
      </div>
      
      {/* Status & Actions */}
      <div className="flex items-center gap-2 sm:gap-3">
        {u.has_face ? (
          <>
            <div className="text-right hidden sm:block">
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Terdaftar
              </Badge>
              {u.face_registered_by && (
                <p className="text-xs text-gray-400 mt-1">
                  oleh: {u.face_registered_by}
                </p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleOpenRegistration(u)}
              className="border-indigo-200"
            >
              <Camera className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Ganti</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteFace(u)}
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <Badge className="bg-amber-100 text-amber-700 hidden sm:flex">
              <XCircle className="w-3 h-3 mr-1" />
              Belum
            </Badge>
            <Button
              onClick={() => handleOpenRegistration(u)}
              className="bg-emerald-600 hover:bg-emerald-700"
              size="sm"
            >
              <Camera className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Daftarkan</span>
            </Button>
          </>
        )}
      </div>
    </div>
  );

  // Render for Admin (grouped by school)
  const renderMultiSchoolView = () => (
    <div className="space-y-4">
      {filteredGroups.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Tidak ada data sekolah</p>
          <p className="text-sm">Coba ubah filter atau kata kunci pencarian</p>
          {(searchQuery || filterDistrik !== 'all' || filterFaceStatus !== 'all') && (
            <Button variant="outline" className="mt-4" onClick={clearFilters}>
              Reset Filter
            </Button>
          )}
        </div>
      ) : (
        filteredGroups.map((group) => {
          const filteredUsersInSchool = getFilteredUsersInSchool(group.users);
          if (filteredUsersInSchool.length === 0) return null;
          
          return (
            <Card 
              key={group.sekolah_id} 
              className="border-indigo-100/50 overflow-hidden"
              data-testid={`sekolah-face-group-${group.sekolah_id}`}
            >
              {/* School Header - Clickable */}
              <CardHeader 
                className="cursor-pointer hover:bg-indigo-50/50 transition-colors py-4"
                onClick={() => toggleSekolah(group.sekolah_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-indigo-950">
                        {group.sekolah_nama}
                      </CardTitle>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {filteredUsersInSchool.length} guru
                        </span>
                        {group.distrik && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {group.distrik}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Stats Badges */}
                    <div className="hidden md:flex items-center gap-2">
                      {group.stats.with_face > 0 && (
                        <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {group.stats.with_face} Terdaftar
                        </Badge>
                      )}
                      {group.stats.without_face > 0 && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">
                          <XCircle className="w-3 h-3 mr-1" />
                          {group.stats.without_face} Belum
                        </Badge>
                      )}
                    </div>
                    
                    {/* Expand Icon */}
                    {expandedSekolah === group.sekolah_id ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Mobile Stats */}
                <div className="flex md:hidden items-center gap-2 mt-2">
                  {group.stats.with_face > 0 && (
                    <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                      {group.stats.with_face} Terdaftar
                    </Badge>
                  )}
                  {group.stats.without_face > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 text-xs">
                      {group.stats.without_face} Belum
                    </Badge>
                  )}
                </div>
              </CardHeader>

              {/* Expanded User List */}
              {expandedSekolah === group.sekolah_id && (
                <CardContent className="pt-0 pb-4">
                  <div className="border-t border-indigo-100 pt-4 space-y-3">
                    {filteredUsersInSchool.map((u) => renderUserItem(u))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })
      )}
    </div>
  );

  if (!canAccess) {
    return (
      <div className="p-4 md:p-6">
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">
            Anda tidak memiliki akses ke halaman ini. Hanya Admin Dinas dan Kepala Sekolah yang dapat mendaftarkan wajah guru.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="face-registration-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-indigo-950">Pendaftaran Wajah</h1>
            <p className="text-gray-500">
              {isKepalaSekolah 
                ? 'Bantu guru mendaftarkan wajah untuk verifikasi absensi'
                : 'Kelola pendaftaran wajah guru per sekolah'}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={loadData} className="border-indigo-200">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Pengguna</p>
                <p className="text-2xl font-bold text-indigo-950">
                  {isKepalaSekolah ? stats.total : filteredStats.total}
                </p>
                {!isKepalaSekolah && filteredStats.total !== stats.total && (
                  <p className="text-xs text-gray-400">dari {stats.total}</p>
                )}
              </div>
              <Users className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-100/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sudah Terdaftar</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {isKepalaSekolah ? stats.with_face : filteredStats.with_face}
                </p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-100/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Belum Terdaftar</p>
                <p className="text-2xl font-bold text-amber-600">
                  {isKepalaSekolah ? stats.without_face : filteredStats.without_face}
                </p>
              </div>
              <XCircle className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6 border-indigo-100/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={isKepalaSekolah ? "Cari nama atau NIP..." : "Cari sekolah, nama, NIP..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            
            {/* Distrik filter (Admin only) */}
            {!isKepalaSekolah && (
              <Select value={filterDistrik} onValueChange={setFilterDistrik}>
                <SelectTrigger className="w-[180px]" data-testid="distrik-filter">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Semua Distrik" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Distrik</SelectItem>
                  {DISTRIK_LIST.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Face status filter */}
            <Select value={filterFaceStatus} onValueChange={setFilterFaceStatus}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Status Wajah" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="without">Belum Terdaftar</SelectItem>
                <SelectItem value="with">Sudah Terdaftar</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear filters button */}
            {(searchQuery || filterDistrik !== 'all' || filterFaceStatus !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Reset
              </Button>
            )}
          </div>
          
          {/* Summary */}
          {!loading && !isKepalaSekolah && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-indigo-100 text-sm">
              <span className="text-gray-500">
                Menampilkan <span className="font-semibold text-indigo-950">{filteredGroups.length}</span> sekolah
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : isKepalaSekolah ? (
        renderSingleSchoolView()
      ) : (
        renderMultiSchoolView()
      )}

      {/* Face Registration Modal */}
      <Dialog open={showRegistrationModal} onOpenChange={setShowRegistrationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-indigo-600" />
              Daftarkan Wajah
            </DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <span>
                  Mendaftarkan wajah untuk: <strong>{selectedUser.nama}</strong> ({selectedUser.nip_nuptk})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <FaceVerification
              mode="register"
              onRegistrationComplete={handleFaceCapture}
              onCancel={() => {
                setShowRegistrationModal(false);
                setSelectedUser(null);
              }}
            />
          )}
          
          {saving && (
            <div className="flex items-center justify-center gap-2 text-indigo-600 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Menyimpan data wajah...</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FaceRegistrationPage;
